/**
 * Chartable sensor config - aligned with deviceTypeCanonicalizer sensor fields
 * Used by TemperatureChart and other DataCharts
 */

export type ChartableSensorId =
  | 'temperature'
  | 'humidity'
  | 'watt'
  | 'watt_hour'
  | 'voltage'
  | 'lux'
  | 'pm25'
  | 'pm10'
  | 'aqi'
  | 'co2'

export interface ChartableSensorOption {
  id: ChartableSensorId
  label: string
  unit: string
  base: number
  min: number
  max: number
}

export const CHARTABLE_SENSOR_CONFIG: Record<ChartableSensorId, ChartableSensorOption> = {
  temperature: { id: 'temperature', label: 'Temp', unit: '°C', base: 22, min: 18, max: 32 },
  humidity: { id: 'humidity', label: 'Humidity', unit: '%', base: 50, min: 30, max: 80 },
  watt: { id: 'watt', label: 'Power', unit: 'W', base: 100, min: 0, max: 500 },
  watt_hour: { id: 'watt_hour', label: 'Energy', unit: 'Wh', base: 500, min: 0, max: 2000 },
  voltage: { id: 'voltage', label: 'Voltage', unit: 'V', base: 110, min: 90, max: 130 },
  lux: { id: 'lux', label: 'Lux', unit: 'lux', base: 300, min: 0, max: 1000 },
  pm25: { id: 'pm25', label: 'PM2.5', unit: 'µg/m³', base: 25, min: 0, max: 200 },
  pm10: { id: 'pm10', label: 'PM10', unit: 'µg/m³', base: 40, min: 0, max: 300 },
  aqi: { id: 'aqi', label: 'AQI', unit: '', base: 80, min: 0, max: 500 },
  co2: { id: 'co2', label: 'CO2', unit: 'ppm', base: 600, min: 400, max: 2000 },
}

/** Mock sensor options when no deviceSN - use all chartable sensors */
export const MOCK_SENSOR_OPTIONS: ChartableSensorOption[] = Object.values(CHARTABLE_SENSOR_CONFIG)

const SENSOR_KEY_ALIASES: Record<string, ChartableSensorId> = {
  temp: 'temperature',
  temperature: 'temperature',
  temperatureambientcelsius: 'temperature',
  temperature_ambient_celsius: 'temperature',
  ambienttemperaturecelsius: 'temperature',
  ambient_temperature_celsius: 'temperature',
  currenttemperaturecelsius: 'temperature',
  current_temperature_celsius: 'temperature',
  thermostattemperatureambient: 'temperature',
  thermostat_temperature_ambient: 'temperature',
  thermostattemperaturesetpoint: 'temperature',
  thermostat_temperature_setpoint: 'temperature',
  targettemperature: 'temperature',
  target_temperature: 'temperature',
  currenttemperature: 'temperature',
  current_temperature: 'temperature',
  setpoint: 'temperature',
  humidity: 'humidity',
  rh: 'humidity',
  humidityambientpercent: 'humidity',
  humidity_ambient_percent: 'humidity',
  ambienthumiditypercent: 'humidity',
  ambient_humidity_percent: 'humidity',
  relativehumidity: 'humidity',
  relative_humidity: 'humidity',
  watt: 'watt',
  power: 'watt',
  real_power: 'watt',
  watt_hour: 'watt_hour',
  energy: 'watt_hour',
  wh: 'watt_hour',
  voltage: 'voltage',
  lux: 'lux',
  illuminance: 'lux',
  lightlevel: 'lux',
  pm25: 'pm25',
  pm2_5: 'pm25',
  pm10: 'pm10',
  aqi: 'aqi',
  co2: 'co2',
}

function normalizeSensorKey(rawKey: string): string {
  return String(rawKey || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function unwrapSensorValue(raw: unknown): unknown {
  if (Array.isArray(raw)) {
    if (raw.length === 0) return null
    return unwrapSensorValue(raw[0])
  }
  if (raw && typeof raw === 'object') {
    const rec = raw as Record<string, unknown>
    if ('value' in rec) return unwrapSensorValue(rec.value)
  }
  return raw
}

function hasChartableValue(raw: unknown): boolean {
  const value = unwrapSensorValue(raw)
  if (value == null) return false
  if (typeof value === 'number') return Number.isFinite(value)
  if (typeof value === 'boolean') return true
  if (typeof value === 'string') {
    const text = value.trim()
    if (!text) return false
    if (['-', '--', 'n/a', 'na', 'null', 'undefined'].includes(text.toLowerCase())) return false
    const numeric = Number(text)
    return Number.isFinite(numeric)
  }
  return false
}

/** Get chartable sensor options. When deviceSN provided + credentials, fetch from API; otherwise use mock */
export async function getChartableSensors(
  deviceSN?: string | null,
  credentials?: { apiKey?: string; accessToken?: string; appId?: string }
): Promise<ChartableSensorOption[]> {
  if (!deviceSN?.trim()) {
    return Promise.resolve(MOCK_SENSOR_OPTIONS)
  }

  const { apiKey, accessToken, appId } = credentials || {}
  if (!apiKey && !accessToken) {
    return Promise.resolve(MOCK_SENSOR_OPTIONS)
  }

  try {
    const { queryDeviceStatusWithAuthContext } = await import('../../services/turingApi')
    const res = await queryDeviceStatusWithAuthContext(
      { apiKey, accessToken, appId },
      deviceSN.trim()
    )
    if (!res.success || !res.data?.length) return []

    const chartableIds = new Set(Object.keys(CHARTABLE_SENSOR_CONFIG) as ChartableSensorId[])
    const found = new Set<ChartableSensorId>()

    for (const device of res.data) {
      for (const state of device.states || []) {
        if (!state.values) continue
        for (const [key, entry] of Object.entries(state.values)) {
          const normalized = normalizeSensorKey(key)
          const sensorId = SENSOR_KEY_ALIASES[normalized]
          if (sensorId && chartableIds.has(sensorId)) {
            if (!hasChartableValue(entry)) continue
            found.add(sensorId)
          }
        }
      }
    }

    if (found.size === 0) return []

    return Array.from(found)
      .sort((a, b) => MOCK_SENSOR_OPTIONS.findIndex((s) => s.id === a) - MOCK_SENSOR_OPTIONS.findIndex((s) => s.id === b))
      .map((id) => CHARTABLE_SENSOR_CONFIG[id])
  } catch {
    return []
  }
}
